import { ReactNode, useContext } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { useTranslation } from 'libs/i18n'
import { NavLink } from 'libs/router'
import { ContextMenu, Currency, PayoutDestination } from 'shared/components'
import { Badge } from 'shared/atoms'
import { StoreSettingsContext } from 'store'
import { routes } from 'config/routes'
import type { Option } from 'shared/components/ContextMenu/types'
import type { CardTableColumn } from 'styles/table'
import type { UserListItem } from 'features/users/types/users.types'

type UseColumnsProps = {
	menuOptions: Option[]
}

const MAX_BOOKINGS_COUNT = 2

const cell = (render: (user: UserListItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as UserListItem)

const muted = 'block truncate text-[12px] tabular-nums text-muted-foreground'
const numeric = 'block whitespace-nowrap text-right text-[12px] tabular-nums text-muted-foreground'
const chip =
	'rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground'
const dash = <span className="text-muted-foreground">—</span>

export const useColumns = ({ menuOptions }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const storeCtx = useContext(StoreSettingsContext)
	const currency = storeCtx?.storeSettings?.currency

	return [
		{
			label: t('Reference'),
			width: '90px',
			renderCell: cell((item) => (
				<NavLink
					className="text-[13px] font-semibold tabular-nums text-primary hover:underline"
					to={routes.userEdit(String(item.id))}>
					{item.id}
				</NavLink>
			)),
			select: true,
			pinLeft: true
		},
		{
			label: t('Name'),
			width: 'minmax(150px, 1fr)',
			renderCell: cell((item) => (
				<span className="block truncate text-[13px] font-medium text-foreground">
					{item.name || '—'}
				</span>
			))
		},
		{
			label: t('Email'),
			width: 'minmax(180px, 1.2fr)',
			renderCell: cell((item) => <span className={muted}>{item.email}</span>)
		},
		{
			label: t('Phone'),
			width: '130px',
			renderCell: cell((item) =>
				item.phone_number ? (
					<span className={muted}>{formatPhoneNumberIntl(item.phone_number)}</span>
				) : (
					dash
				)
			)
		},
		{
			label: t('Payout method'),
			width: '180px',
			renderCell: cell((item) => <PayoutDestination destination={item.default_payout_method} />)
		},
		{
			label: t('Current bookings'),
			width: '150px',
			renderCell: cell((item) =>
				item.current_bookings?.length ? (
					<span className="flex items-center gap-1">
						{item.current_bookings.slice(0, MAX_BOOKINGS_COUNT).map((booking) => (
							<NavLink
								key={booking.id}
								className={`${chip} transition-colors hover:text-foreground`}
								to={routes.bookingEdit(String(booking.id))}>
								#{booking.ref_nr}
							</NavLink>
						))}
						{item.current_bookings.length > MAX_BOOKINGS_COUNT && (
							<span className={chip}>+{item.current_bookings.length - MAX_BOOKINGS_COUNT}</span>
						)}
					</span>
				) : (
					dash
				)
			)
		},
		{
			label: t('Total'),
			width: '100px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency className={numeric} price={{ amount: item.profit, currency }} />
			))
		},
		{
			label: t('Outstanding debt'),
			width: '140px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency className={numeric} price={{ amount: item.outstanding_debts, currency }} />
			))
		},
		{
			label: t('Balance'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency
					className="block whitespace-nowrap text-right font-display text-[13px] font-bold tabular-nums text-foreground"
					price={{ amount: item.balance, currency }}
				/>
			))
		},
		{
			label: t('Payout'),
			width: '110px',
			renderCell: cell((item) =>
				item.has_payout_request ? (
					<Badge variant="warning" className="px-2.5 text-[11px]">
						{t('Requested')}
					</Badge>
				) : (
					dash
				)
			)
		},
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					dataId={item.id}
					item={item}
					options={menuOptions}
					label={t('Actions for {{ name }}', { name: item.name || item.email })}
				/>
			))
		}
	]
}
